import { transformFieldToV1 } from "@wix/form-transformer";
import { createFieldLabelMediaProperties } from "./label-media.js";
import { DEFAULT_BREAKPOINT, Breakpoint } from "../constants/breakpoints.js";
import { FIELD_TYPES } from "../constants/field-types.js";
const GRID_DEFAULTS = {
  columns: 12,
  rowHeight: 24,
  margin: {
    vertical: 24,
    horizontal: 24
  },
  padding: {
    vertical: 0,
    horizontal: 0
  }
};
function createFormView({
  form,
  path = "",
  stepId,
  recursive = true,
  includeHiddenFields = false,
  isMobile = false,
  breakpoint = DEFAULT_BREAKPOINT,
  useFieldsV2 = false,
  parentId = ""
}) {
  const step = stepId ? form.steps?.find(({ id }) => id === stepId) : form.steps?.[0];
  const layout = step?.layout ?? {};
  const fields = form.fields ?? [];
  const platformizedFields = form.formFields;
  let nameView = "";
  if ("properties" in form) {
    nameView = form.properties?.name ?? "";
  } else if ("name" in form) {
    nameView = form.name ?? "";
  }
  if ((form.fields ?? []).some(
    (field) => field.view?.fieldType === FIELD_TYPES.APPOINTMENT
  )) {
    useFieldsV2 = false;
  }
  const hasSmallLayout = layout.small?.items;
  const isSmallBreakpoint = isMobile && hasSmallLayout;
  const fieldViews = (useFieldsV2 && platformizedFields ? platformizedFields : fields).filter((field) => includeHiddenFields || !field.hidden).filter(
    (field) => layout?.large?.items?.find((item) => field.id === item.fieldId)
  ).reduce((result, field) => {
    const _field = useFieldsV2 && platformizedFields ? transformFieldToV1(field) : field;
    const fieldView = createFieldView(
      _field,
      layout,
      path,
      recursive,
      isMobile,
      isSmallBreakpoint ? Breakpoint.SMALL : breakpoint
    );
    if (fieldView) {
      result.push(fieldView);
    }
    return result;
  }, []).sort(sortFieldViewsByColumn).sort(sortFieldViewsByRow);
  const gridView = createGridView(layout, isMobile);
  const formView = {
    parentId,
    name: nameView,
    grid: gridView,
    fields: fieldViews
  };
  if (isMobile && !hasSmallLayout || breakpoint === Breakpoint.SMALL && !hasSmallLayout) {
    return applyMobileLayout(formView);
  }
  return formView;
}
function applyMobileLayout(form) {
  const { fields } = form.fields.reduce(
    (state, field) => {
      const layout = {
        width: form.grid.columns,
        height: field.layout.height,
        column: 0,
        row: state.row
      };
      state.fields.push({ ...field, layout });
      state.row += field.layout.height;
      return state;
    },
    { row: 0, fields: [] }
  );
  return { ...form, fields };
}
function createFieldView(field, layout, path, recursive, isMobile, breakpoint = DEFAULT_BREAKPOINT) {
  if (!(field.id && field.view?.fieldType)) {
    return;
  }
  if (!layout) {
    return;
  }
  const breakpoints = createLayoutBreakpoints(layout, field.id);
  const activeLayout = breakpoints[breakpoint] ?? breakpoints.large;
  if (!activeLayout) {
    return;
  }
  const fieldTarget = field.target || "";
  const fieldPath = path ? `${path}/${fieldTarget}` : fieldTarget;
  const propertiesView = createPropertiesView(
    field,
    fieldPath,
    recursive,
    isMobile
  );
  return {
    id: field.id,
    fieldType: field.view.fieldType,
    properties: propertiesView,
    layout: activeLayout,
    layoutByBreakpoint: breakpoints,
    target: fieldTarget,
    path: fieldPath
  };
}
function createLayoutBreakpoints(layout, fieldId) {
  const largeItem = layout.large?.items?.find(
    (item) => item.fieldId === fieldId
  );
  const largeLayoutView = largeItem && createLayoutView(largeItem);
  const smallItem = layout.small?.items?.find(
    (item) => item.fieldId === fieldId
  );
  const smallLayoutView = smallItem && createLayoutView(smallItem);
  return {
    large: largeLayoutView,
    small: smallLayoutView
  };
}
function createPropertiesView(field, path, recursive, isMobile) {
  const { hidden, pii, nestedFormId, readOnly } = field;
  const { disabled, defaultValue, fieldType, form, ...rest } = field.view ?? {};
  const { required } = field.validation ?? {};
  const availability = field.validation?.string?.dateOptions?.availability;
  const labelMediaProperties = createFieldLabelMediaProperties(rest);
  const formView = form && recursive ? createFormView({
    form,
    path,
    recursive: false,
    isMobile,
    parentId: field.id
  }) : void 0;
  return {
    disabled: disabled ?? false,
    hidden: hidden ?? false,
    readOnly: readOnly ?? false,
    required: required ?? false,
    pii: pii ?? false,
    defaultValue: defaultValue ?? void 0,
    validation: field.validation ?? {},
    form: formView,
    ...nestedFormId ? { nestedFormId } : {},
    ...rest,
    ...labelMediaProperties,
    ...availability ? { availability } : {}
  };
}
function createLayoutView(layout) {
  const { row, column, width, height } = layout;
  if (row === null || row === void 0 || column === null || column === void 0 || width === null || width === void 0 || height === null || height === void 0) {
    return;
  }
  return { row, column, width, height, static: layout.static };
}
function createGridView(layout, isMobile) {
  const { columns, rowHeight, padding, margin } = layout?.large ?? {};
  let { horizontal: marginHorizontal } = margin ?? {};
  const { vertical: paddingVertical, horizontal: paddingHorizontal } = padding ?? {};
  const gridRowHeight = rowHeight ?? GRID_DEFAULTS.rowHeight;
  if (isMobile) {
    marginHorizontal = 0;
  }
  return {
    columns: columns ?? GRID_DEFAULTS.columns,
    rowHeight: gridRowHeight,
    margin: {
      vertical: margin?.vertical ?? GRID_DEFAULTS.margin.vertical,
      horizontal: marginHorizontal ?? GRID_DEFAULTS.margin.horizontal
    },
    padding: {
      vertical: paddingVertical ?? GRID_DEFAULTS.padding.vertical,
      horizontal: paddingHorizontal ?? GRID_DEFAULTS.padding.horizontal
    }
  };
}
function sortFieldViewsByRow(fieldA, fieldB) {
  return fieldA.layout.row - fieldB.layout.row;
}
function sortFieldViewsByColumn(fieldA, fieldB) {
  return fieldA.layout.column - fieldB.layout.column;
}
export {
  createFormView,
  createPropertiesView
};
//# sourceMappingURL=form-view.js.map
